// Components/Forms/Fields/Controls/TextControl.tsx
import type React from 'react';
import { Form } from 'react-bootstrap';
import { useController, useFormContext } from 'react-hook-form';
import { LoadingControl } from '../LoadingControl';

type TextControlProps = {
  name: string;

  className?: string;
  loading?: boolean;

  placeholder?: string;
  disabled?: boolean;

  /** Input type, e.g. "text", "email", "number" */
  type?: React.HTMLInputTypeAttribute;

  /**
   * Optional parse function (e.g. trim, convert empty string to undefined, etc.)
   * If omitted, raw string value is used.
   */
  parse?: (raw: string) => any;

  /**
   * Optional: override invalid state (rarely needed).
   * By default this comes from RHF fieldState.
   */
  isInvalid?: boolean;
};

export const TextControl = ({
  name,
  className,
  loading,
  placeholder,
  disabled,
  type = 'text',
  parse,
  isInvalid,
}: TextControlProps) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name: name as any, control });

  const coerce = (raw: string) => {
    if (!parse) return raw;
    return parse(raw);
  };

  if (loading) return <LoadingControl />;

  return (
    <Form.Control
      className={className}
      disabled={disabled}
      type={type}
      placeholder={placeholder}
      isInvalid={isInvalid ?? fieldState.invalid}
      value={field.value ?? ''}
      onChange={(e) => field.onChange(coerce(e.target.value))}
      onBlur={field.onBlur}
      name={field.name}
      ref={field.ref}
    />
  );
};
